<template>
  <div class="!flex justify-center p-4 cursor-default" @click.stop.prevent>
    <div class="container w-full" dir="ltr">
      <div
        class="h-full max-w-screen-md mx-auto space-y-4 py-20 overflow-y-auto"
      >
        <template v-if="title">
          <div>
            <TheNuxtLink :to="linkTo(title)">
              <TitleCard :title="title" />
            </TheNuxtLink>
          </div>
          <!--          <NamiButton-->
          <!--            button-type="primary"-->
          <!--            block-->
          <!--            :href="linkTo(readerManager.titleEntity)"-->
          <!--          >{{-->
          <!--              translate(-->
          <!--                "components.reader.SubscribeCard.buttons.return_to_title"-->
          <!--              )-->
          <!--            }}-->
          <!--          </NamiButton>-->
        </template>
        <Card>
          <h1 class="text-lg font-bold mb-4">
            {{
              translate(
                "components.reader.SubscribeCard.headers.locked_to_subscribers",
              )
            }}
          </h1>
          <ChapterCard
            class="min-w-0"
            v-if="chapter"
            no-link
            :chapter="chapter"
            :fallback-url="coverUrl"
            is-locked
          >
            <template #contextMenu>
              <div></div>
            </template>
          </ChapterCard>
        </Card>
        <Card v-if="org">
          <h1 class="text-lg font-bold mb-4">
            {{
              minSubTier
                ? "The following tier is required to view this chapter:"
                : "Support the organization here:"
            }}
          </h1>
          <template v-if="minSubTier">
            <NuxtLink :to="linkTo(org) + '/subscriptions'">
              <SubscriptionListCard
                :tier="minSubTier"
                no-details
                class="input-border"
              />
            </NuxtLink>
            <NamiButton
              class="mt-4"
              button-type="primary"
              block
              :href="linkTo(org) + '/subscriptions'"
              >{{
                translate(
                  "components.reader.SubscribeCard.buttons.subscribe_to_tier",
                )
              }}
            </NamiButton>
          </template>
          <template v-else>
            <TheNuxtLink :to="linkTo(org) + '/subscriptions'">
              <OrganizationCard :org="org" no-link />
            </TheNuxtLink>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { SubscribeLockedPageGroup } from "~/src/reader/managers/pageGroup";
import { parseTitleForDisplay } from "~/utils/display/title";
import {
  Gating,
  type GatingStrategyParamMap,
  getRelationship,
  isTitle,
  Organization,
  type TitleEntity,
} from "~/src/api";
import { getReaderChapter } from "~/src/reader/managers/chapter";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { useTitleGet } from "~/utils/tanstack/title";
import { getSingleEntityFromCache } from "~/utils/tanstack";
import { getReaderTitle } from "~/src/reader/managers/title";

interface Props {
  page: SubscribeLockedPageGroup;
}

interface Emits {
  (e: "openComments"): void;

  (e: "toggleFollow"): void;
}

const props = defineProps<Props>();
defineEmits<Emits>();

const nuxtApp = useNuxtApp();
const translate = nuxtApp.$i18n.global.t;
const queryClient = useQueryClient();

const { data: chapter, isPending: chapterPending } = getReaderChapter(
  () => queryClient,
  props.page.chapterId,
);
const fetchedTitleId = computed(
  () => chapter.value?.relationships.find(isTitle)?.id ?? "",
);
const titleResolved = computed(() => !!fetchedTitleId.value);

const { data: title } = getReaderTitle(() => queryClient, fetchedTitleId);

const { coverUrl } = parseTitleForDisplay(title, "small");

const { data } = useQuery({
  queryKey: ["gating_condition", "collection", "title", fetchedTitleId],
  queryFn: async () =>
    await Gating.getConditions("title", unref(fetchedTitleId)),
  enabled: titleResolved,
});

const org = computed(() => {
  if (!chapter.value) return null;
  return getRelationship(chapter.value, "organization", true);
});

const { data: subTiers, error } = useAsyncData(
  `org-tiers-${org.value?.id}`,
  async () => {
    if (!org.value?.id) throw new Error("No org id");

    return await Organization.getSubscriptionTiers(org.value.id, {
      order: { amount: "asc" },
      currency: "USD",
      showDeleted: false,
    });
  },
);

const minSubTier = computed(() => {
  if (!data.value || !subTiers.value) return null;

  const condition = data.value.data.find(
    (cond) =>
      cond.attributes.strategy === "subscribed_tier_amount" ||
      cond.attributes.strategy === "subscribed_to_organization",
  );
  if (!condition) return null;
  if (condition.attributes.strategy === "subscribed_to_organization")
    return null;

  const minAmount = (
    condition.attributes
      .parameters as GatingStrategyParamMap["subscribed_tier_amount"]
  ).amount;
  return (
    subTiers.value.data.find(
      (tier) => tier.attributes.minAmounts.USD! >= minAmount,
    ) || null
  );
});
</script>
